import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { AppShell } from '@/components/layout/AppShell'
import { ExportGuideSection } from '@/components/imports/ExportGuideSection'
import { PreviewCard } from '@/components/imports/PreviewCard'
import { ResultCard } from '@/components/imports/ResultCard'
import { SourceCard } from '@/components/imports/SourceCard'
import { listAccounts } from '@/lib/accountsApi'
import { commitImport, previewImport } from '@/lib/importsApi'
import { useToastStore } from '@/stores/toast'

interface BackendErrorPayload {
  detail?: string
}

interface ToastMapping {
  message: string
  detail?: string
}

function mapBackendError(error: unknown): ToastMapping {
  if (!(error instanceof AxiosError)) {
    return { message: 'Unbekannter Fehler' }
  }
  const status = error.response?.status
  const detail = (error.response?.data as BackendErrorPayload | undefined)?.detail

  switch (status) {
    case 400:
      return { message: 'Broker passt nicht zum Account', detail }
    case 403:
      return { message: 'Kein Zugriff auf diesen Account', detail }
    case 413:
      return { message: 'Datei größer als 10 MB' }
    case 422:
      return { message: 'CSV-Format ungültig', detail }
    default:
      if (status && status >= 500) {
        return { message: 'Server-Fehler — bitte später erneut versuchen' }
      }
      if (error.code === 'ECONNABORTED') {
        return { message: 'Zeitüberschreitung — yfinance reagiert langsam' }
      }
      return { message: 'Netzwerkfehler' }
  }
}

export function ImportsPage() {
  const queryClient = useQueryClient()
  const pushToast = useToastStore((s) => s.pushToast)

  const [accountId, setAccountId] = useState<number | null>(null)
  const [file, setFile] = useState<File | null>(null)

  const accountsQuery = useQuery({ queryKey: ['accounts'], queryFn: listAccounts })

  const previewMutation = useMutation({
    mutationFn: ({
      file,
      accountId,
      brokerType,
    }: {
      file: File
      accountId: number
      brokerType: string
    }) => previewImport(file, accountId, brokerType),
    onError: (err) => {
      const m = mapBackendError(err)
      pushToast({ kind: 'error', message: m.message, detail: m.detail })
    },
  })

  const commitMutation = useMutation({
    mutationFn: ({
      file,
      accountId,
      brokerType,
    }: {
      file: File
      accountId: number
      brokerType: string
    }) => commitImport(file, accountId, brokerType),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounts'] })
      queryClient.invalidateQueries({ queryKey: ['positions'] })
      queryClient.invalidateQueries({ queryKey: ['alerts'] })
    },
    onError: (err) => {
      const m = mapBackendError(err)
      pushToast({ kind: 'error', message: m.message, detail: m.detail })
    },
  })

  const view: 'source' | 'preview' | 'result' = commitMutation.data
    ? 'result'
    : previewMutation.data
      ? 'preview'
      : 'source'

  function selectedBrokerType(): string | null {
    const acc = accountsQuery.data?.find((a) => a.id === accountId)
    return acc?.broker_type ?? null
  }

  function handleSubmit() {
    if (!file || accountId === null) return
    const brokerType = selectedBrokerType()
    if (!brokerType) return
    previewMutation.mutate({ file, accountId, brokerType })
  }

  function handleConfirm() {
    if (!file || accountId === null) return
    const brokerType = selectedBrokerType()
    if (!brokerType) return
    commitMutation.mutate({ file, accountId, brokerType })
  }

  function handleCancel() {
    previewMutation.reset()
  }

  function handleNewImport() {
    setFile(null)
    previewMutation.reset()
    commitMutation.reset()
  }

  function handleAccountChange(id: number | null) {
    setAccountId(id)
    if (view !== 'source') previewMutation.reset()
  }

  function handleFileChange(f: File | null) {
    setFile(f)
    if (view !== 'source') previewMutation.reset()
  }

  return (
    <AppShell>
      <div className="mx-auto max-w-5xl">
        <h1 className="tracked-caps mb-6 text-sm text-ink">CSV-Import</h1>

        {view === 'result' && commitMutation.data ? (
          <ResultCard result={commitMutation.data} onNewImport={handleNewImport} />
        ) : (
          <>
            <SourceCard
              accountId={accountId}
              file={file}
              onAccountChange={handleAccountChange}
              onFileChange={handleFileChange}
              onSubmit={handleSubmit}
              disabled={view === 'preview'}
              isLoading={previewMutation.isPending}
            />
            {view === 'preview' && previewMutation.data && (
              <PreviewCard
                preview={previewMutation.data}
                onConfirm={handleConfirm}
                onCancel={handleCancel}
                isCommitting={commitMutation.isPending}
              />
            )}
            {view === 'source' && <ExportGuideSection selectedBrokerType={selectedBrokerType()} />}
          </>
        )}
      </div>
    </AppShell>
  )
}
